import React, { useState, useMemo } from 'react';
import { Box, Flex, TextInput, Button } from '@kistech/kis-ui';
import { CreateVenuePayload } from 'store/venues';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps } from '../../types';
import { getVenueInitialForm, venueValidationSchema, getHasEmptyInput, getHasInvalidInput } from '../../utils';

export const CreateVenueForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getVenueInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateVenuePayload = {
      name: form.name,
      address: form.address
    };

    onSubmit(payload);
  };

  const onInputChange = (key: keyof ReturnType<typeof getVenueInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }
    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getVenueInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await venueValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getVenueInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <Flex justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <Flex direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <MuiTypography className={classes.createNewClientHeading}>Create New Venue</MuiTypography>
          </Box>

          <MuiBox className={classes.createClientName}>
            <TextInput
              label="name"
              size="medium"
              fullWidth
              placeholder="name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              validate={validate('name')}
              isError={validation.name === INVALID}
            />
          </MuiBox>
          <MuiBox className={classes.createClientName}>
            <TextInput
              label="address"
              size="medium"
              fullWidth
              placeholder="address"
              value={form.address}
              onChange={(e) => onInputChange('address', e)}
              onBlur={(e) => onInputBlur('address', e, e.target.value.trim())}
              validate={validate('address')}
              isError={validation.address === INVALID}
            />
          </MuiBox>
        </Grid>

        {error && <MuiTypography className={classes.createClientErrorText}>{error}</MuiTypography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            isDisabled={buttonDisabled}
            size="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Venue
          </Button>
        </MuiBox>
      </Flex>
    </Flex>
  );
};
